import React, { useState, useEffect } from "react";
import { useProducer } from "../context/ProducerContext";
import { useNavigate } from "react-router-dom";
import {
  addCartByCustomer,
  getCartProducts,
} from "../api/cartApi";
import { FaShoppingCart } from "react-icons/fa";

/* 🔒 SAFETY: normalize sizes */
const normalizeSizes = (sizes) => {
  if (Array.isArray(sizes)) return sizes;

  if (typeof sizes === "string") {
    try {
      const parsed = JSON.parse(sizes);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

export default function Dashboard() {
  const { producer, products, categories: apiCategories } = useProducer();
  const navigate = useNavigate();

  const customer = products?.user;
  const productData = products?.products || [];

  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedSizes, setSelectedSizes] = useState({});
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);

  /* ---------- CATEGORIES ---------- */
  const productCategories = productData.map(
    (p) => p.productCategory || p.category
  );
  const apiCategoryList =
    apiCategories?.map((a) => a.productCategory) || [];
  const categories = [
    "All",
    ...new Set([...productCategories, ...apiCategoryList]),
  ];

  /* ---------- LOGOUT ---------- */
  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  /* ---------- LOAD CART ---------- */
  const loadCartFromAPI = async () => {
    try {
      const payload = { customerId: customer.id, producerId: producer.id };
      const res = await getCartProducts(payload);
      setCart(res.data || []);
    } catch (error) {
      console.error("Fetch Cart Error:", error);
    }
  };

  useEffect(() => {
    if (customer?.id && producer?.id) loadCartFromAPI();
  }, [customer, producer]);

  useEffect(() => {
    if (showCart) loadCartFromAPI();
  }, [showCart]);

  /* ---------- ADD / UPDATE CART ---------- */
  const updateCart = async (product, sizeObj, quantity) => {
    if (!sizeObj) return;

    const payload = {
      customerId: customer.id,
      producerId: producer.id,
      productId: product.productId,
      productSize: sizeObj.size,
      unit_price: sizeObj.price,
      quantity,
      total_price: quantity * sizeObj.price,
    };

    try {
      await addCartByCustomer(payload);
      loadCartFromAPI();
    } catch (err) {
      console.error("Cart API Error:", err);
    }
  };

  /* ---------- FILTER ---------- */
  const filteredProducts = productData.filter((p) => {
    const matchesSearch = p.productName
      .toLowerCase()
      .includes(search.toLowerCase());
    const category = p.productCategory || p.category;
    const matchesCategory =
      selectedCategory === "All" || category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="bg-gray-100 h-screen flex flex-col">
      {/* ---------- NAVBAR ---------- */}
      <nav className="bg-white shadow p-4 flex justify-between items-center">
        <div>
          <h1 className="text-xl font-semibold">{producer?.name}</h1>
          <p className="text-sm text-gray-500">{producer?.city}</p>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowCart(true)}
            className="relative bg-gray-100 p-2 rounded-full"
          >
            <FaShoppingCart size={24} />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-2 rounded-full">
                {cartCount}
              </span>
            )}
          </button>

          <button
            onClick={handleLogout}
            className="bg-red-500 text-white px-4 py-2 rounded"
          >
            Logout
          </button>
        </div>
      </nav>

      {/* ---------- PRODUCTS ---------- */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 flex gap-4">
          <input
            className="border px-4 py-2 rounded w-full"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="border px-3 py-2 rounded"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categories.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredProducts.map((product) => {
            const sizeList = normalizeSizes(product.sizes);
            const selectedSize =
              selectedSizes[product.productId] || sizeList[0];

            const qty =
              cart.find(
                (c) =>
                  c.productId === product.productId &&
                  c.productSize === selectedSize?.size
              )?.quantity || 0;

            return (
              <div key={product.productId} className="bg-white shadow rounded-lg">
                <img
                  src={product.productImage}
                  alt={product.productName}
                  className="h-40 w-full object-cover"
                />

                <div className="p-4">
                  <h2 className="font-semibold">{product.productName}</h2>

                  <select
                    className="border px-3 py-2 rounded w-full mt-2"
                    value={selectedSize?.size}
                    onChange={(e) => {
                      const newSize = sizeList.find(
                        (s) => s.size === e.target.value
                      );
                      setSelectedSizes((prev) => ({
                        ...prev,
                        [product.productId]: newSize,
                      }));
                    }}
                  >
                    {sizeList.length === 0 ? (
                      <option disabled>No sizes</option>
                    ) : (
                      sizeList.map((s) => (
                        <option key={s.size} value={s.size}>
                          {s.size} — Rs.{s.price}
                        </option>
                      ))
                    )}
                  </select>

                  <div className="flex gap-3 mt-3 items-center">
                    <button
                      onClick={() =>
                        updateCart(product, selectedSize, qty - 1)
                      }
                      className="px-3 bg-gray-200 rounded"
                    >
                      -
                    </button>

                    <span>{qty}</span>

                    <button
                      onClick={() =>
                        updateCart(product, selectedSize, qty + 1)
                      }
                      className="px-3 bg-gray-200 rounded"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showCart && (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
    <div className="bg-white p-6 rounded-lg w-96 shadow-lg max-h-[90vh] flex flex-col">

      <h2 className="text-xl font-semibold mb-4">Your Cart</h2>

      <div className="flex-1 overflow-y-auto pr-2">
        {cart.length === 0 ? (
          <p className="text-gray-600">Cart is empty</p>
        ) : (
          cart.map((item) => {
            const product = productData.find(
              (p) => p.productId === item.productId
            );

            const sizeList = normalizeSizes(product?.sizes);
            const sizeObj =
              sizeList.find((s) => s.size === item.productSize) ||
              sizeList[0];

            if (!product || !sizeObj) return null;

            return (
              <div key={item.cartId} className="border-b py-3">
                <p className="font-medium">{product.productName}</p>

                {/* SIZE CHANGE */}
                <select
                  className="border px-2 py-1 rounded w-full mt-1"
                  value={item.productSize}
                  onChange={async (e) => {
                    const newSize = e.target.value;
                    const newSizeObj = sizeList.find(
                      (s) => s.size === newSize
                    );

                    // await updateCartSize({
                    //   customerId: customer.id,
                    //   producerId: producer.id,
                    //   productId: item.productId,
                    //   quantity: item.quantity,
                    //   oldSize: item.productSize,
                    //   newSize,
                    //   newPrice: newSizeObj.price,
                    // });

                    loadCartFromAPI();
                  }}
                >
                  {sizeList.map((s) => (
                    <option key={s.size} value={s.size}>
                      {s.size} — Rs.{s.price}
                    </option>
                  ))}
                </select>

                {/* QTY */}
                <div className="flex gap-3 mt-2 items-center">
                  <button
                    onClick={() =>
                      updateCart(product, sizeObj, item.quantity - 1)
                    }
                    className="px-3 bg-gray-200 rounded"
                  >
                    -
                  </button>

                  <span>{item.quantity}</span>

                  <button
                    onClick={() =>
                      updateCart(product, sizeObj, item.quantity + 1)
                    }
                    className="px-3 bg-gray-200 rounded"
                  >
                    +
                  </button>
                </div>

                <p className="text-sm mt-1">
                  Total: Rs.{item.quantity * sizeObj.price}
                </p>
              </div>
            );
          })
        )}
      </div>

      {/* FOOTER */}
      <div className="mt-4 flex justify-between font-semibold">
        <span>Total:</span>
        <span>
          Rs.
          {cart.reduce((sum, item) => {
            const product = productData.find(
              (p) => p.productId === item.productId
            );
            const sizeObj = normalizeSizes(product?.sizes).find(
              (s) => s.size === item.productSize
            );
            return sum + (sizeObj ? item.quantity * sizeObj.price : 0);
          }, 0)}
        </span>
      </div>

      <div className="mt-4 flex gap-3">
        <button
          className="flex-1 bg-gray-700 text-white py-2 rounded"
          onClick={() => setShowCart(false)}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
}
